import type { PolicySlotResponse } from './types'

/**
 * 슬롯(자정 기준 초) ↔ 운영 시간 변환. 정시 아닌 경계 지원은 이 모듈만 바꾸면 되도록 여기에 모은다.
 * 운영 시간 h(0..23) ↔ startSecond h*3600. 23시 = 82800..86400 (운영일 D 의 전날 23시).
 */
export const SECONDS_PER_HOUR = 3600
export const HOURS_PER_DAY = 24
const SECONDS_PER_DAY = SECONDS_PER_HOUR * HOURS_PER_DAY

export interface SlotRange {
  startSecond: number
  endSecondExclusive: number
}

const isHour = (hour: number): boolean => Number.isInteger(hour) && hour >= 0 && hour < HOURS_PER_DAY

export const hourToSlotRange = (hour: number): SlotRange => {
  if (!isHour(hour)) throw new Error(`운영 시간은 0~23 이어야 합니다: ${hour}`)
  return { startSecond: hour * SECONDS_PER_HOUR, endSecondExclusive: (hour + 1) * SECONDS_PER_HOUR }
}

/**
 * 정시 경계 슬롯을 덮는 운영 시간 목록으로 펼친다 (예: 0~86400 → 0..23).
 * 정시가 아닌 경계·범위 밖·역전 구간은 명시 오류로 조회를 실패시킨다.
 */
export const slotToHours = (slot: SlotRange): number[] => {
  const { startSecond, endSecondExclusive } = slot
  if (
    !Number.isInteger(startSecond) ||
    !Number.isInteger(endSecondExclusive) ||
    startSecond % SECONDS_PER_HOUR !== 0 ||
    endSecondExclusive % SECONDS_PER_HOUR !== 0 ||
    startSecond < 0 ||
    endSecondExclusive > SECONDS_PER_DAY ||
    startSecond >= endSecondExclusive
  ) {
    throw new Error(`정시 단위가 아닌 정책 구간(${startSecond}~${endSecondExclusive}초)은 이 화면에서 지원하지 않습니다.`)
  }
  const first = startSecond / SECONDS_PER_HOUR
  return Array.from({ length: (endSecondExclusive - startSecond) / SECONDS_PER_HOUR }, (_, i) => first + i)
}

/** 슬롯 목록을 운영 시간별로 색인한다(여러 시간 슬롯은 시간마다 같은 원본을 가리킨다). 같은 시간 중복은 오류. */
export const indexSlotsByHour = (slots: PolicySlotResponse[] = []): Map<number, PolicySlotResponse> => {
  const byHour = new Map<number, PolicySlotResponse>()
  slots.forEach((slot) => {
    slotToHours(slot).forEach((hour) => {
      if (byHour.has(hour)) throw new Error(`정책 구간이 중복되었습니다: ${hour}시`)
      byHour.set(hour, slot)
    })
  })
  return byHour
}
